import React, { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Route as RouteIcon, User } from "lucide-react";
import { format } from "date-fns";

export default function OwnerRoutes() {
  const [date, setDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [routes, setRoutes] = useState({ routes: [], unassigned: [] });

  useEffect(() => {
    http.get(`/ops/routes?date=${date}`).then(({ data }) => setRoutes(data));
  }, [date]);

  const totalJobs = routes.routes.reduce((s, r) => s + r.total_jobs, 0) + (routes.unassigned?.length || 0);

  return (
    <div className="space-y-6" data-testid="owner-routes">
      <div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Routes</div>
        <h1 className="font-display text-3xl font-bold text-primary">Route Optimization</h1>
        <p className="text-sm text-muted-foreground mt-1">Grouped by cleaner → community → unit order for shortest walking route.</p>
      </div>

      <Card className="border-border/60"><CardContent className="p-5">
        <div className="flex items-end gap-3 flex-wrap">
          <div><Label className="text-xs">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="routes-date" /></div>
          <div className="ml-auto text-right">
            <div className="text-xs uppercase text-muted-foreground">Jobs</div>
            <div className="font-display text-3xl font-bold text-primary">{totalJobs}</div>
            <div className="text-xs text-muted-foreground">across {routes.routes.length} cleaners</div>
          </div>
        </div>
      </CardContent></Card>

      {routes.routes.length === 0 && (routes.unassigned?.length || 0) === 0 && (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-muted-foreground">
          No visits on {date}.
        </CardContent></Card>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {routes.routes.map((r) => (
          <Card key={r.cleaner_id} className="border-border/60">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center"><User size={16} /></div>
                  <div>
                    <div className="font-display font-semibold text-primary">{r.cleaner_name || "Unassigned"}</div>
                    <div className="text-xs text-muted-foreground">{r.total_jobs} job(s)</div>
                  </div>
                </div>
                <RouteIcon size={18} className="text-secondary" />
              </div>
              <div className="space-y-3">
                {r.legs.map((leg, i) => (
                  <div key={i} className="border-l-2 border-secondary/40 pl-3">
                    <div className="text-xs uppercase tracking-wider text-secondary font-semibold flex items-center gap-1"><MapPin size={11} /> {leg.community} · {leg.job_count} stop(s)</div>
                    <ol className="mt-2 space-y-1">
                      {leg.jobs.map((j, idx) => (
                        <li key={j.id} className="text-xs flex items-start gap-2">
                          <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] flex-shrink-0 mt-0.5">{idx + 1}</span>
                          <div>
                            <div className="font-medium">Unit {j.unit_number || "—"} <span className="text-muted-foreground">· {j.plan}</span></div>
                            <div className="text-muted-foreground">{j.address}</div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {routes.unassigned && routes.unassigned.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40"><CardContent className="p-5">
          <div className="font-display font-semibold text-amber-800 mb-2">Unassigned jobs ({routes.unassigned.length})</div>
          <div className="grid md:grid-cols-2 gap-2">
            {routes.unassigned.map((j) => (
              <div key={j.id} className="text-xs bg-background border border-amber-200 rounded-lg p-2">
                <div className="font-semibold text-primary">{j.community_name || "Off-campus"} · Unit {j.unit_number || "—"}</div>
                <div className="text-muted-foreground">{j.address} · {j.plan}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
